/**
 * Rotation angle estimation from pose landmarks.
 *
 * Combines three signals to estimate body rotation:
 * 1. Shoulder X-span ratio (cosine of angle)
 * 2. Shoulder Z-depth difference (rotation direction)
 * 3. Landmark visibility asymmetry (front vs back quadrant)
 *
 * Uses exponential moving average for smooth tracking.
 */
import type { Landmark, PoseLandmarks } from '@/types/measurements';

const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;

/** Target angles for keyframe capture (every 30 degrees). */
export const TARGET_ANGLES = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

/** Tolerance in degrees for considering a target angle "hit". */
export const ANGLE_TOLERANCE = 18;

/** Snap threshold: angles below this with no Z-rotation snap to 0°/180°. */
const SNAP_THRESHOLD = 20;

export interface AngleEstimate {
  /** Estimated rotation angle in degrees [0, 360). 0 = front. */
  angle: number;
  /** Confidence in the estimate [0, 1]. */
  confidence: number;
  /** Whether the person is turning clockwise. */
  clockwise: boolean;
}

export class AngleDetector {
  /** Front-facing reference shoulder span (normalized). */
  private referenceSpan: number;
  /** Smoothed angle estimate (EMA). */
  private smoothedAngle = 0;
  /** EMA smoothing factor. Lower = smoother, higher = more responsive. */
  private alpha = 0.3;
  /** Previous raw angle for direction detection. */
  private prevAngle = 0;
  /** Whether we've received the first frame. */
  private initialized = false;

  /**
   * @param referenceShoulderSpan - Shoulder span from the front photo (normalized X distance).
   *   If not provided, will calibrate from the first frame.
   */
  constructor(referenceShoulderSpan?: number) {
    this.referenceSpan = referenceShoulderSpan ?? 0;
  }

  /**
   * Estimate rotation angle from current pose landmarks.
   */
  estimate(landmarks: PoseLandmarks): AngleEstimate | null {
    const lShoulder = landmarks[LEFT_SHOULDER];
    const rShoulder = landmarks[RIGHT_SHOULDER];
    const lHip = landmarks[LEFT_HIP];
    const rHip = landmarks[RIGHT_HIP];

    if (!lShoulder || !rShoulder ||
        lShoulder.visibility < 0.1 || rShoulder.visibility < 0.1) {
      return null;
    }

    // Signal 1: Shoulder X-span ratio → |cos(angle)|
    const shoulderSpan = Math.abs(rShoulder.x - lShoulder.x);

    // Calibrate reference from first frame if not provided
    if (this.referenceSpan <= 0) {
      this.referenceSpan = shoulderSpan;
    }

    const spanRatio = Math.min(shoulderSpan / this.referenceSpan, 1.0);
    // spanRatio ≈ |cos(angle)|: max at front (0°) and back (180°), min at sides (90°, 270°)

    // Signal 2: Shoulder Z-depth difference → rotation direction
    const zDiff = rShoulder.z - lShoulder.z;
    // Positive zDiff = right shoulder further = turning right (clockwise from above)

    // Signal 3: Visibility asymmetry → front vs back quadrant
    const leftBodyVis = avgVisibility(landmarks, [LEFT_SHOULDER, LEFT_HIP]);
    const rightBodyVis = avgVisibility(landmarks, [RIGHT_SHOULDER, RIGHT_HIP]);
    // Both high = front; both low = back; asymmetric = side

    // Hip span ratio as secondary signal (more stable than shoulders for large rotations)
    const hipSpan = lHip && rHip ? Math.abs(rHip.x - lHip.x) : shoulderSpan;
    const hipSpanRatio = this.referenceSpan > 0
      ? Math.min(hipSpan / (this.referenceSpan * 0.7), 1.0) // hips ~70% of shoulder width
      : spanRatio;

    // Combine span ratios
    const combinedSpanRatio = (spanRatio + hipSpanRatio) / 2;

    // Derive angle from acos of span ratio
    const absAngle = Math.acos(Math.max(0, Math.min(1, combinedSpanRatio))) * (180 / Math.PI);

    // Determine quadrant from Z-depth and visibility
    let rawAngle: number;
    const avgVis = (leftBodyVis + rightBodyVis) / 2;
    const isFacingCamera = avgVis > 0.5;

    // Z-difference threshold: below this, treat as facing directly front/back
    const zSignificant = Math.abs(zDiff) > 0.01;

    if (isFacingCamera) {
      // Front half (0-90° or 270-360°)
      if (!zSignificant) {
        // Facing directly forward, angle ≈ 0
        rawAngle = absAngle < SNAP_THRESHOLD ? 0 : absAngle;
      } else {
        rawAngle = zDiff > 0 ? absAngle : (360 - absAngle) % 360;
      }
    } else {
      // Back half (90-270°)
      if (!zSignificant) {
        rawAngle = absAngle < SNAP_THRESHOLD ? 180 : 180;
      } else {
        rawAngle = zDiff > 0 ? (180 - absAngle) : (180 + absAngle);
      }
    }

    // Normalize to [0, 360)
    rawAngle = ((rawAngle % 360) + 360) % 360;

    // Exponential moving average with wraparound handling
    if (!this.initialized) {
      this.smoothedAngle = rawAngle;
      this.prevAngle = rawAngle;
      this.initialized = true;
    } else {
      this.smoothedAngle = emaAngle(this.smoothedAngle, rawAngle, this.alpha);
    }

    // Direction detection
    const clockwise = angleDiff(rawAngle, this.prevAngle) > 0;
    this.prevAngle = rawAngle;

    // Confidence based on landmark visibility and span ratio clarity
    const visConfidence = Math.min(
      (lShoulder.visibility + rShoulder.visibility) / 2,
      1.0
    );
    const confidence = visConfidence * 0.7 + (1 - Math.abs(combinedSpanRatio - 0.5) * 0.5) * 0.3;

    return {
      angle: Math.round(this.smoothedAngle * 10) / 10,
      confidence: Math.round(confidence * 100) / 100,
      clockwise,
    };
  }

  /** Get the current smoothed angle. */
  get currentAngle(): number {
    return this.smoothedAngle;
  }

  /** Check if a target angle has been reached. */
  isTargetReached(targetAngle: number): boolean {
    const diff = Math.abs(angleDiff(this.smoothedAngle, targetAngle));
    return diff <= ANGLE_TOLERANCE;
  }

  /** Find the next uncaptured target angle closest to the current direction. */
  findNextTarget(capturedAngles: Set<number>): number | null {
    const uncaptured = TARGET_ANGLES.filter((a) => !capturedAngles.has(a));
    if (uncaptured.length === 0) return null;

    // Find closest uncaptured target
    let closest = uncaptured[0]!;
    let closestDist = Math.abs(angleDiff(this.smoothedAngle, closest));

    for (const target of uncaptured) {
      const dist = Math.abs(angleDiff(this.smoothedAngle, target));
      if (dist < closestDist) {
        closest = target;
        closestDist = dist;
      }
    }

    return closest;
  }

  /** Reset the detector state. */
  reset(): void {
    this.smoothedAngle = 0;
    this.prevAngle = 0;
    this.initialized = false;
  }
}

// ─── Helpers ──────────────────────────────────────────────

/** Average visibility of landmarks at specified indices. */
function avgVisibility(landmarks: PoseLandmarks, indices: number[]): number {
  let total = 0;
  let count = 0;
  for (const idx of indices) {
    const lm = landmarks[idx] as Landmark | undefined;
    if (lm) {
      total += lm.visibility;
      count++;
    }
  }
  return count > 0 ? total / count : 0;
}

/**
 * Signed angular difference (b - a) in degrees, [-180, 180].
 * Positive = clockwise, negative = counter-clockwise.
 */
export function angleDiff(a: number, b: number): number {
  let diff = b - a;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}

/**
 * Exponential moving average with circular wraparound.
 * Handles the 359° → 1° transition correctly.
 */
function emaAngle(current: number, target: number, alpha: number): number {
  const diff = angleDiff(current, target);
  const result = current + alpha * diff;
  return ((result % 360) + 360) % 360;
}
